import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  auditTime,
  catchError,
  tap,
  of,
  animationFrameScheduler,
  distinctUntilChanged,
  map,
  debounceTime,
  switchMap,
} from "rxjs";
import PropTypes from "prop-types";
import Track from "../../models/Track";

function CoverageOverlay({ track }) {
  const containerRef = useRef(null);
  const [label, setLabel] = useState(null);

  const findLabel = useCallback(
    (xPos) => {
      const { absoluteOffsetY, pTop, contentHeight } = track;
      const { meshes, zoomService } = track;
      const { y: scrollTop } = zoomService.zoomId$.value;
      const { r2 } = zoomService.yScale$.value;
      const topDist = pTop + absoluteOffsetY + scrollTop + contentHeight + 2;
      const y = Math.min(r2, topDist);
      const raycasted = track.stage.mainScene.raycast(meshes, xPos, y);
      const cov = raycasted?.item?.val ?? null;
      return cov == null ? null : Math.round(cov);
    },
    [setLabel, track]
  );

  useEffect(() => {
    function reposition(pxPos) {
      if (!pxPos || !containerRef.current) return;
      containerRef.current.style.transform = `translateX(${pxPos}px)`;
    }

    const sub1 = track.zoomService.xVisScale$
      .pipe(
        debounceTime(800),
        switchMap(() => track.zoomService.mousePosX$),
        map(findLabel)
      )
      .subscribe(setLabel);

    const sub2 = track.zoomService.mousePosX$
      .pipe(
        distinctUntilChanged(),
        auditTime(0, animationFrameScheduler),
        tap(reposition),
        distinctUntilChanged(),
        auditTime(600),
        map(findLabel),
        distinctUntilChanged(),
        catchError(() => of(null))
      )
      .subscribe(setLabel);

    return () => {
      sub1.unsubscribe();
      sub2.unsubscribe();
    };
  }, [findLabel]);

  return (
    <div
      ref={containerRef}
      className="tw-pointer-events-none tw-text-3xs tw-relative color-1 tw-px-1"
    >
      {label || label === 0 ? (
        <div className="anim-fade-in anim-duration-200">
          <div className="tw-opacity-50 tw-inline-block">COVERAGE:</div>
          <div className="tw-inline-block tw-px-2 tw-font-bold">{label}</div>
        </div>
      ) : null}
    </div>
  );
}

CoverageOverlay.propTypes = {
  track: PropTypes.instanceOf(Track).isRequired,
};

CoverageOverlay.defaultProps = {};

export default CoverageOverlay;
